import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { IonIcon, IonToast, useIonAlert } from '@ionic/react';
import './FileDownload.css';
import {File} from '../../interfaces/File';
import {RouteProps} from 'react-router';
import {downloadOutline, trashBinSharp} from 'ionicons/icons';
import firebaseService from '../../services/firebaseService';
import {useState} from 'react';
import Panel from '../ui/Panel';

interface FileProps extends RouteProps {
    downloadableFiles: File[]
}

/** The query page sets --accent; History has no single database, so it falls back. */
const ACCENT_BY_PATH: Record<string, string> = {
    '/Data/Detector': 'var(--db-red)',
    '/Data/Probe': 'var(--db-orange)',
    '/Data/Incidents': 'var(--db-green)',
    '/Data/Waze': 'var(--db-blue)',
};

const FileDownload: React.FC<FileProps> = (props: FileProps) => {
    const [confirmDelete] = useIonAlert();
    const location = useLocation().pathname;
    const [errorResponse, setErrorResponse] = useState(false);
    props.downloadableFiles.sort((a, b) => dateStringToTimestamp(b.queryDate) - dateStringToTimestamp(a.queryDate));

    const accent = ACCENT_BY_PATH[location] ?? 'var(--db-grey)';
    const showDatabase = location === "/History";

    const deleteFile = (file: File) => {
        confirmDelete({
            header: 'Are you sure?',
            message: 'This will permanently delete this file.',
            buttons: [
                'Cancel',
                { text: 'Delete', handler: () => firebaseService.deleteFile(file.fileName).catch(err => console.log(err))}
            ]
        })
    };

    // This function is required because the backend stores the date as a weird format string - it is specific to that format: Jun 29, 2021 12:36AM
    // TODO: Have someone change the backend to store the queryDate as a firebase timestamp so this isn't necessary
    function dateStringToTimestamp(dateString: string): number {
        const dateYear = parseInt(dateString.substring(8, 12), 10);
        const dateMonth = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].indexOf(dateString.substring(0, 3));
        const dateDay = parseInt(dateString.substring(4, 6), 10);
        let dateHour = parseInt(dateString.substring(13, 15), 10);
        const dateMinute = parseInt(dateString.substring(16, 18), 10);
        const dateMeridiam = dateString.substring(18, 20);
        if (dateMeridiam === 'PM') {
            dateHour += 12;
            if (dateHour > 23) {
                dateHour = 12;
            }
        }
        if (dateMeridiam === 'AM' && dateHour === 12) {
            dateHour = 0;
        }
        return new Date(dateYear, dateMonth, dateDay, dateHour, dateMinute).valueOf();
    }

    return (
        <>
            {props.downloadableFiles.length > 0 && (
                <Panel
                    title="Your downloads"
                    subtitle={`${props.downloadableFiles.length} file${props.downloadableFiles.length === 1 ? '' : 's'}, newest first`}
                    accent={accent}
                    icon={<IonIcon icon={downloadOutline} />}
                >
                    <table className="download-table">
                        <thead>
                            <tr>
                                <th>Status</th>
                                <th>Downloads and reports</th>
                                {showDatabase && <th>Database</th>}
                                <th>Query date</th>
                                <th className="download-table__actions">Delete</th>
                            </tr>
                        </thead>
                        <tbody>
                            {props.downloadableFiles.map((file, i) => (
                                <tr key={i}>
                                    <td>
                                        {file.ready ? (
                                            <a className="download-link" download="Query File" href={file.location}>
                                                <IonIcon icon={downloadOutline} aria-hidden="true" />
                                                Download
                                            </a>
                                        ) : (
                                            <span className="download-status-pill download-status-pill--pending">
                                                Preparing
                                            </span>
                                        )}
                                    </td>
                                    <td>{file.queryResponse}</td>
                                    {showDatabase && <td>{file.page}</td>}
                                    <td className="download-table__date">{file.queryDate}</td>
                                    <td className="download-table__actions">
                                        <button
                                            type="button"
                                            className="download-delete"
                                            onClick={() => deleteFile(file)}
                                            aria-label={`Delete ${file.fileName}`}
                                        >
                                            <IonIcon icon={trashBinSharp} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Panel>
            )}
            <IonToast
                isOpen={errorResponse}
                onDidDismiss={() => setErrorResponse(false)}
                color="danger"
                message="This file no longer exists. It has been removed from your downloads."
                cssClass="toast-error-download"
                duration={10000}
            />
        </>
    );
};

export default FileDownload;
